import React, { useMemo, useState } from 'react';
import {
  Card, Form, InputGroup, Table,
} from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import {
  AgroOverviewLocation,
  LocationsSortKey,
} from 'types/Meteorology/Agro/Overview/Dashboard';
import {
  selectAgroOverviewTableSearch,
  selectAgroOverviewTableSortBy,
  selectAgroOverviewTableSortDirection,
  setTableSearch,
  setTableSortBy,
} from 'redux/reducers/meteorologyAgroOverviewDashboardReducer';
import {
  NEUTRAL_COLOR,
  colorForArmValue,
} from 'helper/Meteorology/Agro/Overview/colorScale';
import {
  LOCATION_COLUMNS,
  RISK_LABEL,
  filterAndSortLocations,
  removerSufixoUf,
} from 'helper/Meteorology/Agro/Overview/locationsTable';
import CentralizedSpinner from 'components/CentralizedSpinner';
import DashboardDropdown from '../Dropdown';
import InfoButton from '../InfoButton';
import InfoModal from '../InfoModal';
import { SearchIcon } from '../icons';

import './index.scss';

// Derivado da fonte única — trocar um label = editar LOCATION_COLUMNS no helper.
const SORT_OPTIONS: Array<{ value: LocationsSortKey; label: string }> = LOCATION_COLUMNS.map(
  (c) => ({ value: c.key, label: c.sortLabel ?? c.label }),
);

// Alinhamento é apresentação: fica no componente, fora da fonte única.
// Decisão 10/08/2026: só Localidade à esquerda; demais colunas centralizadas.
const TH_CLASS: Record<LocationsSortKey, string> = {
  diasSemChuva: 'text-center',
  armPct: 'text-center',
  chuva7dMm: 'text-center',
  risco7d: 'text-center',
  perdaAgua7dMm: 'text-center',
};

const COLUMN_INFO_DESC: Record<LocationsSortKey, string> = {
  diasSemChuva: 'Número consecutivo de dias sem precipitação registrada na localidade.',
  armPct: 'Armazenamento atual de água no solo (% da capacidade máxima).',
  chuva7dMm: 'Precipitação acumulada prevista para os próximos 7 dias.',
  risco7d: 'Classificação de risco de estresse hídrico para os próximos 7 dias, baseada em ARM, tendência de chuva e evapotranspiração. Baixo = condições favoráveis · Moderado = atenção recomendada · Alto = intervenção pode ser necessária · Muito alto = intervenção urgente.',
  perdaAgua7dMm: 'Saldo hídrico acumulado previsto para os próximos 7 dias: negativo indica perda de água, positivo indica ganho.',
};

function countLabel(loading: boolean, locations: AgroOverviewLocation[]): string {
  if (loading) return 'Carregando localidades monitoradas';
  if (locations.length === 0) return 'Nenhuma localidade monitorada';
  if (locations.length === 1) return '1 localidade monitorada';
  return `${locations.length} localidades monitoradas`;
}

export default function LocationsTable({
  locations,
  loading,
  isEmptyState,
}: {
  locations: AgroOverviewLocation[];
  loading: boolean;
  isEmptyState: boolean;
}) {
  const dispatch = useDispatch();
  const search = useSelector(selectAgroOverviewTableSearch);
  const sortBy = useSelector(selectAgroOverviewTableSortBy);
  const sortDirection = useSelector(selectAgroOverviewTableSortDirection);

  const [showInfo, setShowInfo] = useState(false);

  // Mesma regra compartilhada com o ExportButton (export CSV WYSIWYG).
  const filteredSorted = useMemo(
    () => filterAndSortLocations(locations, search, sortBy, sortDirection),
    [locations, search, sortBy, sortDirection],
  );

  return (
    <Card className="agro-overview-locations-card h-100">
      <Card.Body className="d-flex flex-column gap-3">
        <div className="d-flex justify-content-between align-items-end gap-3">
          <div className="locations-title">
            <span>Localidades monitoradas</span>
            <InfoButton
              onClick={() => setShowInfo(true)}
              label="Metodologia: Localidades monitoradas"
            />
            {/* Badge SEMPRE visível — 0 explícito no estado vazio ("não há
                localidade" ≠ "não carregou"); skeleton durante o loading. */}
            <span className="locations-count-badge" aria-label={countLabel(loading, locations)}>
              {loading
                ? <span className="locations-count-skeleton" />
                : <span aria-hidden="true">{locations.length}</span>}
            </span>
          </div>
          <DashboardDropdown
            label="Ordenar por"
            value={sortBy}
            onChange={(next) => dispatch(setTableSortBy(next))}
            options={SORT_OPTIONS}
          />
        </div>
        <InputGroup size="sm">
          <InputGroup.Text className="search-icon-wrapper">
            <SearchIcon title="Buscar" />
          </InputGroup.Text>
          <Form.Control
            placeholder="Buscar localidade"
            value={search}
            onChange={(e) => dispatch(setTableSearch(e.target.value))}
          />
        </InputGroup>
        <div className="locations-table-scroll flex-grow-1">
          {loading ? <CentralizedSpinner /> : (
            <Table size="sm" className="agro-overview-locations-table mb-0">
              <thead>
                <tr>
                  {/* Caixa alta via CSS (text-transform) — o texto fica na fonte única. */}
                  <th>Localidade</th>
                  {LOCATION_COLUMNS.map((c) => (
                    <th key={c.key} className={TH_CLASS[c.key]}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Estado vazio (§4 do contrato): linha única com CTA. */}
                {isEmptyState ? (
                  <tr>
                    <td colSpan={6} className="text-center empty-state-cell">
                      Nenhum local cadastrado
                      {' — '}
                      <Link to="/agro/register">Cadastre um local</Link>
                    </td>
                  </tr>
                ) : filteredSorted.map((loc) => (
                  <tr key={loc.id}>
                    {/* title = tooltip com o nome completo quando o teto de
                        largura aplicar reticências (nomes livres longos). */}
                    <td
                      title={loc.estado
                        ? `${removerSufixoUf(loc.nome, loc.estado)} / ${loc.estado}`
                        : loc.nome}
                    >
                      <span
                        className="location-bullet"
                        style={{
                          backgroundColor: loc.armPct === null
                            ? NEUTRAL_COLOR
                            : colorForArmValue(loc.armPct),
                        }}
                      />
                      {removerSufixoUf(loc.nome, loc.estado)}
                      {loc.estado ? ` / ${loc.estado}` : ''}
                    </td>
                    <td className="text-center">{loc.diasSemChuva ?? '—'}</td>
                    <td className="text-center">
                      {loc.armPct === null ? '—' : `${loc.armPct}%`}
                    </td>
                    <td className="text-center">{loc.chuva7dMm ?? '—'}</td>
                    <td className="text-center">
                      {loc.risco7d === null ? (
                        <span className="risk-badge risk-badge-null">—</span>
                      ) : (
                        <span className={`risk-badge risk-badge-${loc.risco7d}`}>
                          {RISK_LABEL[loc.risco7d]}
                        </span>
                      )}
                    </td>
                    {/* DECISÃO-10: cor condicional (apenas valores não-nulos) —
                        vermelho < 0, verde >= 0.
                        DECISÃO-9: null → '—' com cor neutra da tabela. */}
                    {loc.perdaAgua7dMm === null ? (
                      <td className="text-center">—</td>
                    ) : (
                      <td
                        className={`text-center ${loc.perdaAgua7dMm < 0
                          ? 'perda-cell-negativa'
                          : 'perda-cell-positiva'}`}
                      >
                        {`${loc.perdaAgua7dMm.toFixed(1)} mm`}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card.Body>

      <InfoModal
        title="Localidades Monitoradas"
        show={showInfo}
        onHide={() => setShowInfo(false)}
      >
        <p>
          Pontos de monitoramento agrometeorológico das principais regiões
          produtoras brasileiras, com indicadores atualizados diariamente.
        </p>

        <div className="info-section-title">Indicadores da tabela</div>

        {LOCATION_COLUMNS.map(({ key, label }) => (
          <div key={key} className="info-col-row">
            <span className="info-col-name">{label}</span>
            <span className="info-col-desc">{COLUMN_INFO_DESC[key]}</span>
          </div>
        ))}
      </InfoModal>
    </Card>
  );
}
